import { getApiUrl } from "../services/apiConfig.js";
import React, { useState, useRef, useEffect } from "react";
const menuNotificationState = (() => {
  const areas = Object.freeze(["tarefas", "desafios", "meritos", "rankings"]);
  const storageName = "dm-menu-read-v1";
  const emptyDots = () => ({
    tarefas: false,
    desafios: false,
    meritos: false,
    rankings: false,
  });
  const decimal = (value) =>
    typeof value === "string" && /^(0|[1-9][0-9]{0,63})$/.test(value);
  const compareCursor = (left, right) =>
    left.length - right.length || left.localeCompare(right);
  const handleAction = (value) =>
    typeof value === "string" && /^[A-Za-z0-9_.:-]{1,96}$/.test(value);
  function normalize(body, expected) {
    const value = body?.data;
    const scope = value?.scope;
    const sections = value?.sections;
    if (
      body?.status !== "success" ||
      value?.schema !== 1 ||
      !scope ||
      !sections ||
      !Number.isSafeInteger(scope.student_id) ||
      scope.student_id !== expected.studentId ||
      !handleAction(scope.tenant_id) ||
      !handleAction(scope.epoch) ||
      (expected.tenantId !== null && scope.tenant_id !== expected.tenantId) ||
      !Array.isArray(value.allowed) ||
      value.allowed.length > 4 ||
      new Set(value.allowed).size !== value.allowed.length ||
      value.allowed.some((area) => !areas.includes(area))
    )
      throw Error("Invalid notification scope");
    const result = {
      scope: {
        student_id: scope.student_id,
        tenant_id: scope.tenant_id,
        epoch: scope.epoch,
      },
      allowed: [...value.allowed],
      sections: {},
    };
    for (const area of ["tarefas", "desafios"]) {
      if (!decimal(sections[area]?.cursor))
        throw Error("Invalid notification cursor");
      result.sections[area] = {
        cursor: sections[area].cursor,
      };
    }
    const earned = sections.meritos?.earned_ids;
    if (
      !Array.isArray(earned) ||
      earned.length > 256 ||
      earned.some((item) => !handleAction(item)) ||
      new Set(earned).size !== earned.length
    )
      throw Error("Invalid earned merits");
    result.sections.meritos = {
      earned_ids: [...earned].sort(),
    };
    const rank = sections.rankings;
    if (
      !rank ||
      !handleAction(rank.scope) ||
      !handleAction(rank.period_key) ||
      !(
        rank.position === null ||
        (Number.isSafeInteger(rank.position) && rank.position > 0)
      )
    )
      throw Error("Invalid ranking position");
    let position = rank.position;
    if (
      Object.prototype.hasOwnProperty.call(rank, "position_base") ||
      Object.prototype.hasOwnProperty.call(rank, "ties")
    ) {
      if (
        position !== null ||
        !Number.isSafeInteger(rank.position_base) ||
        rank.position_base < 1 ||
        !Array.isArray(rank.ties) ||
        !rank.ties.length ||
        rank.ties.length > 4096 ||
        rank.ties.some(
          (row) =>
            !Number.isSafeInteger(row?.student_id) ||
            row.student_id < 1 ||
            typeof row.name !== "string" ||
            row.name.length > 200,
        ) ||
        new Set(rank.ties.map((row) => row.student_id)).size !==
          rank.ties.length ||
        !rank.ties.some((row) => row.student_id === expected.studentId)
      )
        throw Error("Invalid ranking tie projection");
      // er sorts ties by the browser's name.localeCompare. Integer student keys are
      // inserted by Object.values in ascending order, so stable equal-name ties use ID.
      const ties = [...rank.ties].sort(
        (left, right) =>
          left.name.localeCompare(right.name) ||
          left.student_id - right.student_id,
      );
      position =
        rank.position_base +
        ties.findIndex((row) => row.student_id === expected.studentId);
      if (!Number.isSafeInteger(position))
        throw Error("Invalid ranking tie position");
    }
    result.sections.rankings = {
      scope: rank.scope,
      period_key: rank.period_key,
      position,
    };
    return result;
  }
  const scopeKey = (origin, scope) =>
    JSON.stringify([
      origin,
      "aluno",
      scope.student_id,
      scope.tenant_id,
      scope.epoch,
    ]);
  function baseline(summary) {
    return {
      version: 1,
      cursors: {
        tarefas: summary.sections.tarefas.cursor,
        desafios: summary.sections.desafios.cursor,
      },
      merits: [...summary.sections.meritos.earned_ids],
      rank: {
        ...summary.sections.rankings,
      },
      pending: emptyDots(),
    };
  }
  function validRecord(value) {
    return (
      value?.version === 1 &&
      decimal(value.cursors?.tarefas) &&
      decimal(value.cursors?.desafios) &&
      Array.isArray(value.merits) &&
      value.merits.length <= 256 &&
      value.merits.every(handleAction) &&
      new Set(value.merits).size === value.merits.length &&
      handleAction(value.rank?.scope) &&
      handleAction(value.rank?.period_key) &&
      (value.rank.position === null ||
        (Number.isSafeInteger(value.rank.position) &&
          value.rank.position > 0)) &&
      areas.every((area) => typeof value.pending?.[area] === "boolean")
    );
  }
  function transition(previous, summary, openArea) {
    const next = validRecord(previous)
      ? JSON.parse(JSON.stringify(previous))
      : baseline(summary);
    for (const area of ["tarefas", "desafios"]) {
      const cursor = summary.sections[area].cursor;
      if (compareCursor(cursor, next.cursors[area]) > 0) {
        next.cursors[area] = cursor;
        next.pending[area] = true;
      }
    }
    const known = new Set(next.merits);
    const earned = summary.sections.meritos.earned_ids;
    if (earned.some((value) => !known.has(value))) next.pending.meritos = true;
    // Active configurations may retire merits. Keep the current bounded identity set
    // plus retired IDs within the bounded 256-ID history.
    next.merits = [...new Set([...earned, ...next.merits])].slice(0, 256);
    const rank = summary.sections.rankings;
    const samePeriod =
      next.rank.scope === rank.scope &&
      next.rank.period_key === rank.period_key;
    if (!samePeriod) next.pending.rankings = false;
    else if (
      rank.position !== null &&
      next.rank.position !== null &&
      rank.position < next.rank.position
    )
      next.pending.rankings = true;
    if (rank.position !== null || !samePeriod)
      next.rank = {
        ...rank,
      };
    for (const area of areas) {
      if (!summary.allowed.includes(area) || area === openArea)
        next.pending[area] = false;
    }
    return next;
  }
  function markRead(record, area) {
    if (!validRecord(record) || !areas.includes(area)) return record;
    return {
      ...record,
      pending: {
        ...record.pending,
        [area]: false,
      },
    };
  }
  function read(storage, key) {
    try {
      const raw = storage.getItem(storageName);
      if (!raw || raw.length > 200000) return null;
      const envelope = JSON.parse(raw);
      return envelope?.version === 1 &&
        validRecord(envelope.entries?.[key]?.record)
        ? envelope.entries[key].record
        : null;
    } catch {
      return null;
    }
  }
  function write(storage, key, record, now = Date.now()) {
    if (!validRecord(record)) return;
    try {
      const raw = storage.getItem(storageName);
      let old = null;
      try {
        old = raw && raw.length < 200000 ? JSON.parse(raw) : null;
      } catch {
        /* Replace a corrupt read-marker envelope. */
      }
      const entries =
        old?.version === 1 &&
        old.entries &&
        typeof old.entries === "object" &&
        !Array.isArray(old.entries)
          ? old.entries
          : {};
      entries[key] = {
        at: now,
        record,
      };
      const kept = Object.entries(entries)
        .filter(
          ([keyLength, entry]) =>
            keyLength.length <= 512 && validRecord(entry?.record),
        )
        .sort(
          (left, right) => Number(right[1].at || 0) - Number(left[1].at || 0),
        )
        .slice(0, 16);
      let encoded = JSON.stringify({
        version: 1,
        entries: Object.fromEntries(kept),
      });
      while (encoded.length > 180000 && kept.length > 1) {
        kept.pop();
        encoded = JSON.stringify({
          version: 1,
          entries: Object.fromEntries(kept),
        });
      }
      storage.setItem(storageName, encoded);
    } catch {
      /* A blocked/full browser store still leaves the live dots working. */
    }
  }
  return Object.freeze({
    areas,
    storageName,
    emptyDots,
    normalize,
    scopeKey,
    baseline,
    validRecord,
    transition,
    markRead,
    read,
    write,
  });
})();
/* Included in the existing React module, after its authenticated API client. */
function getMenuNotificationIdentity(student, adminLogged) {
  if (adminLogged || !student?.id) return null;
  try {
    const session = JSON.parse(
      sessionStorage.getItem("auth_session") || "null",
    );
    const data = session?.data;
    if (
      session?.tipo !== "aluno" ||
      Number(data?.id) !== Number(student.id) ||
      !data.learning_token
    )
      return null;
    const studentId = Number(data.id);
    if (!Number.isSafeInteger(studentId) || studentId < 1) return null;
    // currentStudent is refreshed from the DB; its institution can change without a new login.
    // A present null means the unassigned tenant, rather than an absent/stale session field.
    const currentHouse = Object.prototype.hasOwnProperty.call(
      student,
      "casa_id",
    );
    const tenant = currentHouse
      ? (student.casa_id ?? 0)
      : (data.tenant_id ?? data.institution_id ?? data.casa_id ?? null);
    const tenantId = tenant === null ? null : String(tenant);
    // Authorization is only in memory and headers, never in local storage or URLs.
    const headers = {
      "X-Learning-Token": data.learning_token,
      "X-Student-Id": String(studentId),
    };
    return {
      studentId,
      tenantId,
      key: JSON.stringify([studentId, tenantId, data.learning_token]),
      headers,
    };
  } catch {
    return null;
  }
}
function readMenuNotificationStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
function useMenuNotifications(student, adminLogged, view) {
  const identity = getMenuNotificationIdentity(student, adminLogged);
  const authKey = identity?.key || "";
  const [snapshot, setSnapshot] = useState({
    authKey: "",
    scopeKey: "",
    record: null,
  });
  const current = useRef({
    student,
    adminLogged,
    view,
    authKey,
  });
  current.current = {
    student,
    adminLogged,
    view,
    authKey,
  };
  const recordRef = useRef(null);
  const sameIdentity = () =>
    getMenuNotificationIdentity(
      current.current.student,
      current.current.adminLogged,
    )?.key === authKey;
  useEffect(() => {
    recordRef.current = null;
    setSnapshot({
      authKey,
      scopeKey: "",
      record: null,
    });
    if (!identity) return;
    let alive = true;
    let active = null;
    let lastAttempt = -Infinity;
    let acceptedScope = "";
    let generation = 0;
    const memory = new Map();
    const apply = (summary) => {
      if (!alive || !sameIdentity()) return;
      const key = menuNotificationState.scopeKey(
        window.location.origin,
        summary.scope,
      );
      const saved = menuNotificationState.read(
        readMenuNotificationStorage(),
        key,
      );
      const previous =
        (acceptedScope === key ? recordRef.current : null) || saved;
      const record = menuNotificationState.transition(
        previous,
        summary,
        current.current.view,
      );
      acceptedScope = key;
      recordRef.current = record;
      menuNotificationState.write(readMenuNotificationStorage(), key, record);
      setSnapshot({
        authKey,
        scopeKey: key,
        record,
      });
    };
    const refresh = async () => {
      if (
        !alive ||
        !sameIdentity() ||
        document.hidden ||
        active ||
        Date.now() - lastAttempt < 15000
      )
        return;
      lastAttempt = Date.now();
      const abort = new AbortController();
      const requestGeneration = ++generation;
      active = abort;
      const timeout = setTimeout(() => abort.abort(), 10000);
      try {
        const response = await fetch(getApiUrl("/api/menu-notifications.php"), {
          method: "GET",
          credentials: "same-origin",
          cache: "no-store",
          headers: identity.headers,
          signal: abort.signal,
        });
        if (!alive || !sameIdentity() || requestGeneration !== generation)
          return;
        if ([401, 403].includes(response.status)) {
          memory.clear();
          acceptedScope = "";
          recordRef.current = null;
          setSnapshot({
            authKey,
            scopeKey: "",
            record: null,
          });
          return;
        }
        if (!response.ok) return;
        const body = await response.json();
        if (!alive || !sameIdentity() || requestGeneration !== generation)
          return;
        const summary = menuNotificationState.normalize(body, identity);
        memory.set(authKey, summary);
        while (memory.size > 1) memory.delete(memory.keys().next().value);
        apply(summary);
      } catch {
        /* Notices never interrupt studying or hide a previously accepted result on a network failure. */
      } finally {
        clearTimeout(timeout);
        if (active === abort) active = null;
      }
    };
    const changed = (event) => {
      // Providers may attach this exact same compact summary to an already loaded GET.
      // Scope and current login are checked before reusing it; incomplete page arrays are never inferred.
      if (event?.detail?.summary) {
        try {
          const summary = menuNotificationState.normalize(
            event.detail.summary,
            identity,
          );
          // A loaded projection supersedes a poll started before that projection.
          generation++;
          active?.abort();
          active = null;
          lastAttempt = Date.now();
          memory.set(authKey, summary);
          apply(summary);
        } catch {
          /* Ignore an untrusted or stale projection. */
        }
      } else refresh();
    };
    const visible = () => {
      if (!document.hidden) refresh();
    };
    const storageChanged = (event) => {
      if (
        event.key !== menuNotificationState.storageName ||
        !acceptedScope ||
        !sameIdentity()
      )
        return;
      let record = menuNotificationState.read(
        readMenuNotificationStorage(),
        acceptedScope,
      );
      if (record) {
        // Reading in another tab supersedes a poll started before that read marker.
        generation++;
        active?.abort();
        active = null;
        lastAttempt = Date.now();
        if (record.pending[current.current.view]) {
          record = menuNotificationState.markRead(record, current.current.view);
          menuNotificationState.write(
            readMenuNotificationStorage(),
            acceptedScope,
            record,
          );
        }
        recordRef.current = record;
        setSnapshot({
          authKey,
          scopeKey: acceptedScope,
          record,
        });
      }
    };
    window.addEventListener("dm-menu-notifications-changed", changed);
    window.addEventListener("dm-learning-changed", changed);
    window.addEventListener("dm-task-bonus-changed", changed);
    window.addEventListener("focus", visible);
    window.addEventListener("storage", storageChanged);
    document.addEventListener("visibilitychange", visible);
    const timer = setInterval(refresh, 60000);
    refresh();
    return () => {
      alive = false;
      active?.abort();
      memory.clear();
      clearInterval(timer);
      window.removeEventListener("dm-menu-notifications-changed", changed);
      window.removeEventListener("dm-learning-changed", changed);
      window.removeEventListener("dm-task-bonus-changed", changed);
      window.removeEventListener("focus", visible);
      window.removeEventListener("storage", storageChanged);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [authKey]);
  useEffect(() => {
    if (
      !identity ||
      snapshot.authKey !== authKey ||
      !snapshot.scopeKey ||
      !recordRef.current ||
      !menuNotificationState.areas.includes(view) ||
      !sameIdentity()
    )
      return;
    // Also acknowledge navigation through Back, a direct URL or an in-page link.
    const record = menuNotificationState.markRead(recordRef.current, view);
    recordRef.current = record;
    menuNotificationState.write(
      readMenuNotificationStorage(),
      snapshot.scopeKey,
      record,
    );
    setSnapshot((previous) =>
      previous.authKey === authKey
        ? {
            ...previous,
            record,
          }
        : previous,
    );
  }, [authKey, view, snapshot.scopeKey]);
  return identity &&
    snapshot.authKey === authKey &&
    sameIdentity() &&
    snapshot.record
    ? snapshot.record.pending
    : menuNotificationState.emptyDots();
}
window.DMMenuNotifications = Object.freeze({
  refresh() {
    window.dispatchEvent(new Event("dm-menu-notifications-changed"));
  },
});
export {
  menuNotificationState,
  getMenuNotificationIdentity,
  readMenuNotificationStorage,
  useMenuNotifications,
};
